import { FormEvent, useEffect, useMemo, useState } from 'react';
import type {
  AccountWithBuckets,
  AppCurrency,
  ReconciliationWriteContext,
} from '@financial/shared';
import { useAccounts } from '../accounts/useAccounts';
import { useCreateTransfer } from './useTransfers';
import { getApiErrorMessage } from '../../lib/errors';
import { formatMoney, toDateInputValue } from '../../lib/format';
import { Field } from '../../components/ui/Field';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  accountAvailableBalance,
  resolveBalanceTarget,
  targetAvailableBalance,
  transferBalanceChanges,
  type AccountBalanceChange,
  type BalanceTarget,
  type ResolvedBalanceTarget,
} from '../../lib/balancePreview';

function today(): string {
  return toDateInputValue();
}

/** `accountId` o `accountId:bucketId` para poder elegir apartado en un solo select. */
function parseTarget(value: string): { accountId: string; bucketId?: string } {
  const [accountId, bucketId] = value.split(':');
  return { accountId, bucketId: bucketId || undefined };
}

function TargetSelect({
  id,
  label,
  accounts,
  value,
  allowedCurrency,
  excludedAccountId,
  disabled = false,
  autoFocus = false,
  onChange,
}: {
  id: string;
  label: string;
  accounts: AccountWithBuckets[];
  value: string;
  allowedCurrency?: AppCurrency;
  /** Oculta toda una cuenta cuando elegirla no puede cambiar su saldo total. */
  excludedAccountId?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <select
        id={id}
        required
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        data-modal-autofocus={autoFocus ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-600"
      >
        <option value="" disabled>
          Elige cuenta
        </option>
        {accounts
          .filter((account) => account.id !== excludedAccountId)
          .map((a) => {
            const disabled = Boolean(allowedCurrency && a.currency !== allowedCurrency);
            return (
              <optgroup
                key={a.id}
                label={`${a.name} · ${a.currency}${disabled ? ' (otra moneda)' : ''}`}
                disabled={disabled}
              >
                <option value={a.id}>
                  Dinero disponible · {formatMoney(accountAvailableBalance(a), a.currency)}
                </option>
                {(a.buckets ?? []).map((b) => (
                  <option key={b.id} value={`${a.id}:${b.id}`}>
                    Apartado {b.name} · {formatMoney(b.balance, a.currency)}
                  </option>
                ))}
              </optgroup>
            );
          })}
      </select>
    </div>
  );
}

function targetLabel(target: ResolvedBalanceTarget): string {
  return target.bucket
    ? `apartado ${target.bucket.name} de ${target.account.name}`
    : `dinero disponible de ${target.account.name}`;
}

function BalanceChangeRow({
  label,
  before,
  after,
  currency,
}: {
  label: string;
  before: number;
  after: number;
  currency: AppCurrency;
}) {
  const beforeLabel = formatMoney(before, currency);
  const afterLabel = formatMoney(after, currency);
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-600">{label}</dt>
      <dd className="shrink-0 font-semibold tabular-nums text-slate-900">
        <span className="sr-only">
          Pasa de {beforeLabel} a {afterLabel}
        </span>
        <span aria-hidden className="flex items-center gap-1.5">
          <span>{beforeLabel}</span>
          <span className="text-slate-500">→</span>
          <span>{afterLabel}</span>
        </span>
      </dd>
    </div>
  );
}

export function TransferBalancePreview({
  changes,
  from,
  to,
  amount,
}: {
  changes: AccountBalanceChange[];
  from: ResolvedBalanceTarget;
  to: ResolvedBalanceTarget;
  amount: number;
}) {
  return (
    <section
      className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5"
      aria-live="polite"
      aria-atomic="true"
    >
      <h3 className="text-sm font-bold text-slate-900">Revisa antes de guardar</h3>
      <p className="mt-1 text-xs leading-5 text-slate-600">
        Moverás {formatMoney(amount, from.account.currency)} de {targetLabel(from)} a{' '}
        {targetLabel(to)}. No contará como ingreso ni gasto.
      </p>
      <div
        className={`mt-3 grid gap-2 border-t border-slate-200 pt-3 ${changes.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {changes.map((change) => (
          <div key={change.account.id} className="rounded-xl bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="truncate text-xs font-bold text-slate-900">{change.account.name}</h4>
              <span className="text-[11px] font-bold text-slate-600">
                {change.account.currency}
              </span>
            </div>
            <dl className="mt-2 space-y-2 text-xs">
              <BalanceChangeRow
                label={change.account.buckets.length > 0 ? 'Saldo total' : 'Saldo de la cuenta'}
                before={change.total.before}
                after={change.total.after}
                currency={change.account.currency}
              />
              {change.account.buckets.length > 0 && (
                <BalanceChangeRow
                  label="Disponible para usar"
                  before={change.available.before}
                  after={change.available.after}
                  currency={change.account.currency}
                />
              )}
              {change.buckets.map(({ bucket, balance }) => (
                <BalanceChangeRow
                  key={bucket.id}
                  label={`Apartado ${bucket.name}`}
                  before={balance.before}
                  after={balance.after}
                  currency={change.account.currency}
                />
              ))}
            </dl>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-700">
        El total entre tus cuentas no cambia.
      </p>
    </section>
  );
}

export interface TransferFormInitialValues {
  from?: string;
  to?: string;
  amount?: number | string;
  date?: string;
  note?: string;
  /** Protege el traspaso que explica una conciliación contra reintentos y saldos obsoletos. */
  reconciliation?: ReconciliationWriteContext;
}

/**
 * Registra un traspaso entre cuentas propias.
 *
 * No es ingreso ni egreso: el dinero cambia de bolsa y el patrimonio no se
 * mueve, así que no entra en el gasto del mes ni en el presupuesto.
 */
export function TransferForm({
  onDone,
  initialValues,
  lockAccountingFields = false,
  onBusyChange,
}: {
  onDone: () => void;
  initialValues?: TransferFormInitialValues;
  /** Mantiene fijos monto, fecha y la punta que originó una conciliación. */
  lockAccountingFields?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const accountsQ = useAccounts();
  const create = useCreateTransfer();
  const [from, setFrom] = useState(initialValues?.from ?? '');
  const [to, setTo] = useState(initialValues?.to ?? '');
  const [amount, setAmount] = useState(
    initialValues?.amount === undefined ? '' : String(initialValues.amount),
  );
  const [date, setDate] = useState(initialValues?.date ?? today);
  const [note, setNote] = useState(initialValues?.note ?? '');

  useEffect(() => {
    onBusyChange?.(create.isPending);
    return () => onBusyChange?.(false);
  }, [create.isPending, onBusyChange]);

  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const lockFrom = lockAccountingFields && Boolean(initialValues?.from);
  const lockTo = lockAccountingFields && Boolean(initialValues?.to);
  const sameTarget = from !== '' && from === to;
  const numericAmount = Number(amount);
  const amountIsValid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0;
  const fromCoordinates: BalanceTarget | null = from ? parseTarget(from) : null;
  const toCoordinates: BalanceTarget | null = to ? parseTarget(to) : null;
  const sameAccountForReconciliation = Boolean(
    lockAccountingFields &&
    fromCoordinates &&
    toCoordinates &&
    fromCoordinates.accountId === toCoordinates.accountId,
  );
  const fromTarget = fromCoordinates ? resolveBalanceTarget(accounts, fromCoordinates) : null;
  const toTarget = toCoordinates ? resolveBalanceTarget(accounts, toCoordinates) : null;
  const currencyMismatch = Boolean(
    fromTarget && toTarget && fromTarget.account.currency !== toTarget.account.currency,
  );
  const selectedCurrency = fromTarget?.account.currency ?? toTarget?.account.currency;
  const hasAccountsInOtherCurrencies = Boolean(
    selectedCurrency && accounts.some((account) => account.currency !== selectedCurrency),
  );
  const sourceBalance = fromTarget ? targetAvailableBalance(fromTarget) : 0;
  const amountTooLarge =
    amountIsValid && Boolean(fromTarget) && numericAmount > sourceBalance + 0.005;
  const balanceChanges =
    fromCoordinates &&
    toCoordinates &&
    fromTarget &&
    toTarget &&
    amountIsValid &&
    !amountTooLarge &&
    !sameTarget &&
    !sameAccountForReconciliation &&
    !currencyMismatch
      ? transferBalanceChanges(accounts, fromCoordinates, toCoordinates, numericAmount)
      : [];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (
      !fromCoordinates ||
      !toCoordinates ||
      !fromTarget ||
      !toTarget ||
      sameTarget ||
      sameAccountForReconciliation ||
      currencyMismatch ||
      !amountIsValid ||
      amountTooLarge
    )
      return;
    create.mutate(
      {
        fromAccountId: fromCoordinates.accountId,
        fromBucketId: fromCoordinates.bucketId,
        toAccountId: toCoordinates.accountId,
        toBucketId: toCoordinates.bucketId,
        amount: numericAmount,
        date,
        note: note || undefined,
        reconciliation: initialValues?.reconciliation
          ? {
              ...initialValues.reconciliation,
              counterpartyExpectedBalance:
                fromTarget.account.id === initialValues.reconciliation.accountId
                  ? toTarget.account.balance
                  : fromTarget.account.balance,
            }
          : undefined,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-slate-50 p-3 text-sm leading-5 text-slate-600">
        Un traspaso mueve dinero entre tus propias cuentas. No cuenta como gasto ni como ingreso: el
        total de tu dinero no cambia, sólo cambia de lugar.
      </p>

      {lockAccountingFields && (
        <p className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm leading-5 text-slate-700">
          El monto, la fecha y la cuenta conciliada quedan fijos para explicar exactamente la
          diferencia. Elige sólo la otra cuenta y, si quieres, agrega una nota. Al guardarlo quedará
          como evidencia; cualquier corrección posterior se registra como un hecho nuevo.
        </p>
      )}

      <TargetSelect
        id="t-from"
        label="De"
        accounts={accounts}
        value={from}
        allowedCurrency={toTarget?.account.currency}
        excludedAccountId={lockTo ? toCoordinates?.accountId : undefined}
        disabled={lockFrom}
        autoFocus={lockAccountingFields && !lockFrom}
        onChange={setFrom}
      />
      <TargetSelect
        id="t-to"
        label="A"
        accounts={accounts}
        value={to}
        allowedCurrency={fromTarget?.account.currency}
        excludedAccountId={lockFrom ? fromCoordinates?.accountId : undefined}
        disabled={lockTo}
        autoFocus={lockAccountingFields && !lockTo}
        onChange={setTo}
      />
      {selectedCurrency && hasAccountsInOtherCurrencies && !currencyMismatch && (
        <p className="-mt-2 text-xs leading-5 text-slate-600">
          Sólo puedes elegir otra cuenta en {selectedCurrency}; la app no convierte divisas.
        </p>
      )}
      {sameTarget && (
        <p className="text-sm text-expense" role="alert">
          El origen y el destino no pueden ser el mismo.
        </p>
      )}
      {sameAccountForReconciliation && !sameTarget && (
        <p className="text-sm text-expense" role="alert">
          Para explicar la diferencia, elige otra cuenta. Mover dinero entre el disponible y un
          apartado de esta misma cuenta no cambia su saldo total.
        </p>
      )}
      {currencyMismatch && fromTarget && (
        <p
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-5 text-expense"
          role="alert"
        >
          Estas cuentas usan monedas distintas. La app no convierte divisas; elige un destino en{' '}
          {fromTarget.account.currency}.
        </p>
      )}

      <Field
        id="t-amount"
        label="Monto"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        required
        readOnly={lockAccountingFields}
        aria-readonly={lockAccountingFields || undefined}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      {amountTooLarge && fromTarget && (
        <p
          className="-mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-5 text-expense"
          role="alert"
        >
          {fromTarget.bucket
            ? `El apartado ${fromTarget.bucket.name} sólo tiene ${formatMoney(sourceBalance, fromTarget.account.currency)}. Reduce el monto o elige otro origen.`
            : `Sólo tienes ${formatMoney(sourceBalance, fromTarget.account.currency)} disponibles en ${fromTarget.account.name}. Reduce el monto o elige otro origen.`}
        </p>
      )}
      {balanceChanges.length > 0 && fromTarget && toTarget && (
        <TransferBalancePreview
          changes={balanceChanges}
          from={fromTarget}
          to={toTarget}
          amount={numericAmount}
        />
      )}
      <DatePicker
        id="t-date"
        label="Fecha"
        required
        value={date}
        onChange={setDate}
        disabled={lockAccountingFields}
      />
      <Field
        id="t-note"
        label="Nota (opcional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {create.isError && (
        <p className="text-sm text-expense" role="alert">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={
            create.isPending ||
            sameTarget ||
            sameAccountForReconciliation ||
            !fromTarget ||
            !toTarget ||
            currencyMismatch ||
            !amountIsValid ||
            amountTooLarge
          }
          className="tap-target w-full rounded-xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-[0_8px_16px_-8px_rgba(15,23,42,.55)] disabled:opacity-60"
        >
          {create.isPending ? 'Guardando…' : 'Registrar traspaso'}
        </button>
      </div>
    </form>
  );
}
